import React, { useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch, RootStateOrAny } from 'react-redux';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { useValidation } from 'hooks';
import { notify } from 'services/notification';
import { Link } from 'react-router-dom';

import { AuthActions, AuthState } from 'store/ducks/auth';
import { Input } from 'components/shared/Form';
import { firebaseApp } from 'lib/firebaseApp';
import { useFirebaseAuth } from './useFirebaseAuth';
import { FirebaseSignInButton } from './firebase-sign-in-button';

import logoImage from 'assets/images/logo.svg';
import microsoftLogoImage from 'assets/images/microsoft-logo.svg';
import pageBackgroundImageLogin from 'assets/images/login-page-background.jpg';

import * as S from './styles';

const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_KEY as string;
const SHOW_TEST_ENV_WARNING =
  window.location.hostname === 'hml-bassf.webcol.systems';

export const Login: React.FC = () => {
  const [human, setHuman] = useState<string | boolean>(false);
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors } = useValidation();
  const dispatch = useDispatch();
  const { signIn: handleFirebaseSignIn } = useFirebaseAuth();

  const { loading } = useSelector<RootStateOrAny, AuthState>(
    (state) => state.auth
  );

  const onRecaptcha = useCallback((value) => {
    setHuman(value);
  }, []);

  const handleSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          email: Yup.string()
            .required('Informe seu e-mail')
            .email('Informe um e-mail válido'),
          password: Yup.string().required('Informe sua senha'),
        });
        await schema.validate(data, {
          abortEarly: false,
        });

        if (!human) {
          notify(
            'error',
            'Se você é um humano resolva o reCAPTCHA para prosseguir.'
          );
        } else {
          dispatch(AuthActions.loginRequest(data));
        }
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, human]
  );

  return (
    <S.PageWrapper>
      {SHOW_TEST_ENV_WARNING && (
        <S.TestEnvWarning>Ambiente de homologação</S.TestEnvWarning>
      )}
      <S.Main>
        <S.MainSection testEnv={SHOW_TEST_ENV_WARNING}>
          <S.MainSectionFigure>
            <img src={logoImage} alt="logo" />
          </S.MainSectionFigure>
          <S.MainSectionTitle>
            Transportation Management System
          </S.MainSectionTitle>
          <FirebaseSignInButton
            type="button"
            provider="microsoft"
            onSuccess={handleFirebaseSignIn}
            firebaseApp={firebaseApp}
            disabled={loading}
          >
            <img src={microsoftLogoImage} alt="Microsoft logo" /> Entrar com
            Microsoft
          </FirebaseSignInButton>
          <S.TextSeparator>
            <div className="line left"></div>
            <span>ou</span>
            <div className="line right"></div>
          </S.TextSeparator>
          <S.Instructions>
            Digite suas credenciais de acesso abaixo.
            <br />
            Esqueceu sua senha? <Link to="/forgot">Redefinir senha</Link>
          </S.Instructions>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <S.FormContent>
              <Input
                name="email"
                label="E-mail"
                className="first"
                placeholder="Email"
              />
              <Input
                name="password"
                label="Senha"
                type="password"
                className="last"
                placeholder="Senha"
              />
            </S.FormContent>
            <S.ReCaptchaContainer>
              {/* @ts-ignore */}
              <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={onRecaptcha} />
            </S.ReCaptchaContainer>
            <S.SubmitButton type="submit" disabled={loading}>
              Entrar
            </S.SubmitButton>
          </Form>
        </S.MainSection>
        <S.ArtSection>
          <S.ArtSectionBackground
            style={{ backgroundImage: `url(${pageBackgroundImageLogin})` }}
          ></S.ArtSectionBackground>
        </S.ArtSection>
      </S.Main>
    </S.PageWrapper>
  );
};
